import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3, Clock, FileDown, FileSpreadsheet, Image as ImageIcon, Pin, RefreshCw, Target, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { api, downloadBlob, errMsg } from "../../lib/api";
import { exportChartAsPng } from "../../lib/chartExport";
import { CollectionTargetsEditor } from "./CollectionTargetsEditor";

/**
 * Phase 213 — Monthly Collection live graph (Admin Dashboard + Super Admin dashboard).
 * Grouped bars April → March: selected FY (primary) vs the previous FY, side by side per month.
 * Previous-FY bars appear only when that year has receipts. Polls every 60 s. Tooltip (hover / click / tap) shows
 * month, FY, receipts count, total, third-party and direct amounts for each bar.
 * Phase 214 — a dashed target line (the month-wise collection targets set under Settings) is drawn over the bars for the
 * selected FY so gaps stand out; tooltip / pinned panel show target, gap and achievement %. Export: Excel (.xlsx) + PNG.
 */
const REFRESH_MS = 60_000;
const COLOR_CURRENT = "hsl(206,88%,28%)";     // primary (brand blue)
const COLOR_PREVIOUS = "hsl(38,92%,45%)";     // previous FY (amber) — clearly different hue
const COLOR_TARGET = "hsl(152,55%,32%)";      // target line (success green)

const money = (n) => "\u20B9" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `\u20B9${(v / 1e7).toFixed(v >= 1e8 ? 0 : 1)} Cr`;
  if (v >= 1e5) return `\u20B9${(v / 1e5).toFixed(v >= 1e6 ? 0 : 1)} L`;
  if (v >= 1e3) return `\u20B9${(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k`;
  return `\u20B9${v}`;
};
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return m === 1 ? "1 min ago" : `${m} min ago`;
};
const pctText = (p) => (p == null ? "—" : `${Number(p).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);

/** "Short by ₹X" / "Ahead by ₹X" / "On target" for one month row (gap = target − collected). */
function GapLine({ row, testId }) {
  if (row.target == null) return null;
  const gap = Number(row.gap || 0);
  const cls = gap > 0 ? "text-rose-700" : "text-emerald-700";
  const txt = gap > 0 ? `Short by ${money(gap)}` : gap < 0 ? `Ahead by ${money(Math.abs(gap))}` : "On target";
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 pl-4 tabular-nums" data-testid={testId}>
      <dt className="text-muted-foreground">Target</dt><dd className="text-right font-medium" data-testid={`${testId}-amount`}>{money(row.target)}</dd>
      <dt className="text-muted-foreground">Gap to target</dt><dd className={`text-right font-medium ${cls}`} data-testid={`${testId}-gap`}>{txt}</dd>
      <dt className="text-muted-foreground">Achievement</dt><dd className={`text-right ${cls}`} data-testid={`${testId}-pct`}>{pctText(row.achievement_pct)}{row.future ? <span className="text-muted-foreground font-normal"> · upcoming</span> : null}</dd>
    </dl>
  );
}

function BarTooltip({ active, payload, label, fyLabel, prevLabel, testIdPrefix }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const blocks = [];
  if (row.current) blocks.push({ title: fyLabel, color: COLOR_CURRENT, d: row.current, key: "current", when: row.key });
  if (row.previous) blocks.push({ title: prevLabel, color: COLOR_PREVIOUS, d: row.previous, key: "previous", when: row.previous.key });
  return (
    <div className="rounded-lg border bg-popover/95 backdrop-blur-md shadow-lg p-3 text-xs min-w-[240px] space-y-2" data-testid={`${testIdPrefix}-tooltip`} data-month={row.key}>
      <p className="font-semibold text-sm font-heading" data-testid={`${testIdPrefix}-tooltip-month`}>{row.month_name}{row.future ? <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">(upcoming)</span> : null}</p>
      {blocks.map((b) => (
        <div key={b.key} className="space-y-0.5" data-testid={`${testIdPrefix}-tooltip-${b.key}`}>
          <p className="flex items-center gap-1.5 font-medium"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} /> FY {b.title} <span className="text-muted-foreground font-normal">({b.when})</span></p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 pl-4 tabular-nums">
            <dt className="text-muted-foreground">Total receipts</dt><dd className="text-right font-medium" data-testid={`${testIdPrefix}-tooltip-${b.key}-receipts`}>{Number(b.d.receipts || 0).toLocaleString("en-IN")}</dd>
            <dt className="text-muted-foreground">Total collected</dt><dd className="text-right font-semibold" data-testid={`${testIdPrefix}-tooltip-${b.key}-total`}>{money(b.d.total)}</dd>
            <dt className="text-muted-foreground">Third party</dt><dd className="text-right" data-testid={`${testIdPrefix}-tooltip-${b.key}-third-party`}>{money(b.d.third_party)}</dd>
            <dt className="text-muted-foreground">Direct collection</dt><dd className="text-right" data-testid={`${testIdPrefix}-tooltip-${b.key}-direct`}>{money(b.d.direct)}</dd>
          </dl>
          {b.key === "current" && row.target != null && (
            <div className="pt-0.5">
              <p className="flex items-center gap-1.5 font-medium pl-0"><span className="inline-block h-0.5 w-2.5 rounded-sm" style={{ background: COLOR_TARGET }} /> Target · FY {fyLabel}</p>
              <GapLine row={row} testId={`${testIdPrefix}-tooltip-target`} />
            </div>
          )}
        </div>
      ))}
      {blocks.length === 2 && (
        <p className="text-[11px] text-muted-foreground border-t pt-1.5 tabular-nums" data-testid={`${testIdPrefix}-tooltip-delta`}>
          {(() => {
            const diff = (row.current?.total || 0) - (row.previous?.total || 0);
            if (!row.previous?.total && !row.current?.total) return "No collection in either year";
            if (!row.previous?.total) return "No collection in the previous year";
            const pct = Math.round((diff / row.previous.total) * 100);
            return `${diff >= 0 ? "▲" : "▼"} ${money(Math.abs(diff))} (${diff >= 0 ? "+" : ""}${pct}%) vs previous year`;
          })()}
        </p>
      )}
    </div>
  );
}

export function MonthlyCollectionChart({ code, className = "", testIdPrefix = "monthly-collection", title = "Monthly Collection", compactHeader = false,
                                         canEditTargets = false, municipalityName = "" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fyId, setFyId] = useState("");            // "" = server default (FY containing today)
  const [lastAt, setLastAt] = useState(null);
  const [, tick] = useState(0);                     // re-render "x s ago"
  const [pinned, setPinned] = useState(null);       // clicked month key (sticky tooltip on touch devices)
  const [targetsOpen, setTargetsOpen] = useState(false);   // Phase 214: Set targets dialog
  const [exporting, setExporting] = useState(null);        // "xlsx" | "png" | null
  const timer = useRef(null);
  const chartRef = useRef(null);
  const fyRef = useRef(fyId);
  fyRef.current = fyId;

  const load = useCallback(async (silent = false) => {
    if (!code) return;
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/t/${code}/dashboard/admin/monthly-collection`, { params: fyRef.current ? { fy_id: fyRef.current } : {} });
      setData(r.data);
      setError(null);
      setLastAt(new Date().toISOString());
      if (!fyRef.current && r.data?.fy?.id) { fyRef.current = r.data.fy.id; setFyId(r.data.fy.id); }
    } catch (e) {
      setError(errMsg(e));
    } finally { setLoading(false); }
  }, [code]);

  // initial + whenever the FY (or municipality) changes
  useEffect(() => { setData(null); fyRef.current = ""; setFyId(""); }, [code]);
  useEffect(() => { load(false); }, [load, fyId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // live refresh every 60 s (paused while the tab is hidden) + "x s ago" ticker
  useEffect(() => {
    const start = () => {
      clearInterval(timer.current);
      timer.current = setInterval(() => { if (document.visibilityState === "visible") load(true); }, REFRESH_MS);
    };
    start();
    const onVis = () => { if (document.visibilityState === "visible") { load(true); start(); } };
    document.addEventListener("visibilitychange", onVis);
    const t2 = setInterval(() => tick((n) => n + 1), 10_000);
    return () => { clearInterval(timer.current); clearInterval(t2); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const rows = useMemo(() => (data?.months || []).map((m) => ({
    ...m,
    current_total: m.current?.total || 0,
    previous_total: m.previous ? (m.previous.total || 0) : null,
    target: m.target ?? null,
  })), [data]);
  const fyLabel = data?.fy?.fy_label || "";
  const prevLabel = data?.previous_fy?.fy_label || "";
  const hasPrev = !!data?.has_previous;
  const totals = data?.totals;
  const tg = data?.targets;
  const hasTargets = !!tg?.has_targets;
  const empty = data && !rows.some((m) => m.current_total > 0 || (m.previous_total || 0) > 0) && !hasTargets;
  const pinnedRow = pinned ? rows.find((r) => r.key === pinned) : null;
  const muniText = municipalityName || (code || "").toUpperCase();

  // ---------------------------------------------------------------- Phase 214: exports
  const exportXlsx = async () => {
    setExporting("xlsx");
    try {
      await downloadBlob(`/t/${code}/dashboard/admin/monthly-collection/export${data?.fy?.id ? `?fy_id=${encodeURIComponent(data.fy.id)}` : ""}`,
        `monthly-collection_${code}_FY${fyLabel || "current"}.xlsx`);
      toast.success("Excel downloaded", { description: `Monthly comparison for FY ${fyLabel}${hasPrev ? ` vs FY ${prevLabel}` : ""} — one sheet with targets, gaps and achievement %.` });
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(null); }
  };
  const exportPng = async () => {
    setExporting("png");
    try {
      const legend = [{ label: `FY ${fyLabel}`, color: COLOR_CURRENT }];
      if (hasPrev) legend.push({ label: `FY ${prevLabel} (previous)`, color: COLOR_PREVIOUS });
      if (hasTargets) legend.push({ label: "Monthly target", color: COLOR_TARGET, kind: "line" });
      const footer = [
        `FY ${fyLabel}: ${money(totals?.current?.total)} · ${Number(totals?.current?.receipts || 0).toLocaleString("en-IN")} receipts · third party ${money(totals?.current?.third_party)} · direct ${money(totals?.current?.direct)}`,
      ];
      if (hasPrev) footer.push(`FY ${prevLabel}: ${money(totals?.previous?.total)} · ${Number(totals?.previous?.receipts || 0).toLocaleString("en-IN")} receipts`);
      if (hasTargets) footer.push(`Target FY ${fyLabel}: ${money(tg.annual_total)} · achieved ${pctText(totals?.current?.achievement_pct)} · to date ${Number(tg.to_date_gap || 0) > 0 ? `short by ${money(tg.to_date_gap)}` : `ahead by ${money(Math.abs(tg.to_date_gap || 0))}`} (${pctText(tg.to_date_pct)} of ${money(tg.to_date_target)})`);
      footer.push(`Successful receipts by payment date · generated ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · MunicipalConnect`);
      await exportChartAsPng({
        container: chartRef.current,
        title: `${title} — FY ${fyLabel}${hasPrev ? ` vs FY ${prevLabel}` : ""}`,
        subtitle: `${muniText} · month-wise collection (April → March)${hasTargets ? " with monthly targets" : ""}`,
        legend, footerLines: footer,
        filename: `monthly-collection_${code}_FY${fyLabel || "current"}.png`,
      });
      toast.success("PNG image downloaded", { description: "Ready to drop into a review presentation." });
    } catch (e) { toast.error(e?.message || "Could not export the image"); } finally { setExporting(null); }
  };

  return (
    <Card className={`shadow-sm ${className}`} data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-heading flex items-center gap-2 flex-wrap">
              <BarChart3 className="h-4 w-4 text-muted-foreground" /> {title}
              <Badge variant="outline" className="text-[10px] font-normal gap-1 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`${testIdPrefix}-live-badge`}>
                <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600" /></span>
                Live · 60 s
              </Badge>
            </CardTitle>
            {!compactHeader && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Month-wise collection for FY {fyLabel || "…"}{hasPrev ? ` compared with FY ${prevLabel}` : data ? " — no receipts in the previous year yet, so only the current year is shown" : ""}.
                {hasTargets ? " The dashed line is the monthly target." : ""} Hover or tap a bar for receipts, total, third-party and direct amounts{hasTargets ? " and the gap to target" : ""}.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={fyId || data?.fy?.id || ""} onValueChange={(v) => { setPinned(null); setFyId(v); }} disabled={!data?.financial_years?.length}>
              <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`${testIdPrefix}-fy-select`} aria-label="Financial year">
                <SelectValue placeholder="Financial year" />
              </SelectTrigger>
              <SelectContent>
                {(data?.financial_years || []).map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`${testIdPrefix}-fy-option-${f.fy_label}`}>
                    FY {f.fy_label}{f.virtual ? " · not in master" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canEditTargets && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setTargetsOpen(true)} disabled={!data} data-testid={`${testIdPrefix}-set-targets`}>
                <Target className="h-3.5 w-3.5 mr-1.5" /> {hasTargets ? "Edit targets" : "Set targets"}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs" disabled={!data || !!exporting} data-testid={`${testIdPrefix}-export`}>
                  <FileDown className={`h-3.5 w-3.5 mr-1.5 ${exporting ? "animate-pulse" : ""}`} /> {exporting ? "Exporting…" : "Export"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60" data-testid={`${testIdPrefix}-export-menu`}>
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">FY {fyLabel}{hasPrev ? ` vs FY ${prevLabel}` : ""}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={exportXlsx} data-testid={`${testIdPrefix}-export-xlsx`}>
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-700" /> <span>Excel sheet (.xlsx)<span className="block text-[11px] text-muted-foreground">Month, both FYs, target, gap, achievement %, receipts, split</span></span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={exportPng} disabled={empty} data-testid={`${testIdPrefix}-export-png`}>
                  <ImageIcon className="h-4 w-4 mr-2 text-primary" /> <span>Image (.png)<span className="block text-[11px] text-muted-foreground">Chart with title, legend and totals for slides</span></span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => load(false)} disabled={loading} title="Refresh now" data-testid={`${testIdPrefix}-refresh`}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && !data ? (
          <div className="text-sm text-destructive flex items-center justify-between gap-2 py-8" data-testid={`${testIdPrefix}-error`}>
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={() => load(false)} data-testid={`${testIdPrefix}-retry`}>Try again</Button>
          </div>
        ) : !data ? (
          <div className="space-y-2" data-testid={`${testIdPrefix}-loading`}><Skeleton className="h-6 w-2/3" /><Skeleton className="h-64 w-full" /></div>
        ) : (
          <>
            {/* FY totals strip */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-2" data-testid={`${testIdPrefix}-totals`}>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_CURRENT }} />
                FY {fyLabel}: <b className="tabular-nums" data-testid={`${testIdPrefix}-total-current`}>{money(totals?.current?.total)}</b>
                <span className="text-muted-foreground tabular-nums">· {Number(totals?.current?.receipts || 0).toLocaleString("en-IN")} receipts · third party {compact(totals?.current?.third_party)} · direct {compact(totals?.current?.direct)}</span></span>
              {hasPrev && (
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_PREVIOUS }} />
                  FY {prevLabel}: <b className="tabular-nums" data-testid={`${testIdPrefix}-total-previous`}>{money(totals?.previous?.total)}</b>
                  <span className="text-muted-foreground tabular-nums">· {Number(totals?.previous?.receipts || 0).toLocaleString("en-IN")} receipts</span></span>
              )}
              {hasTargets ? (
                <span className="flex items-center gap-1.5" data-testid={`${testIdPrefix}-target-summary`}>
                  <span className="inline-block h-0.5 w-3 rounded-sm" style={{ background: COLOR_TARGET }} />
                  Target: <b className="tabular-nums" data-testid={`${testIdPrefix}-target-annual`}>{money(tg.annual_total)}</b>
                  <span className="text-muted-foreground tabular-nums">· {pctText(totals?.current?.achievement_pct)} achieved</span>
                  {tg.to_date_target > 0 && (
                    <Badge variant="outline" className={`tabular-nums text-[10px] font-normal ${Number(tg.to_date_gap) > 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`${testIdPrefix}-target-to-date`}>
                      To date: {Number(tg.to_date_gap) > 0 ? `short by ${compact(tg.to_date_gap)}` : `ahead by ${compact(Math.abs(tg.to_date_gap || 0))}`} ({pctText(tg.to_date_pct)})
                    </Badge>
                  )}
                </span>
              ) : canEditTargets ? (
                <button type="button" className="text-muted-foreground underline-offset-2 hover:underline hover:text-foreground flex items-center gap-1" onClick={() => setTargetsOpen(true)} data-testid={`${testIdPrefix}-no-targets-hint`}>
                  <Target className="h-3 w-3" /> No monthly targets for FY {fyLabel} — set targets to see the gap line
                </button>
              ) : null}
              {error && <span className="text-amber-700" data-testid={`${testIdPrefix}-stale-note`}>Last refresh failed — showing previous data</span>}
            </div>

            {empty ? (
              <div className="h-72 flex items-center justify-center text-sm text-muted-foreground border rounded-md border-dashed" data-testid={`${testIdPrefix}-empty`}>
                No receipts recorded in FY {fyLabel}{hasPrev ? ` or FY ${prevLabel}` : ""} yet — bars appear as collections come in.
              </div>
            ) : (
              <div ref={chartRef} className="h-72 [&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none [&_svg:focus]:outline-none" data-testid={`${testIdPrefix}-chart`} data-fy={fyLabel} data-has-previous={hasPrev ? "1" : "0"} data-has-targets={hasTargets ? "1" : "0"}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%" barGap={3} style={{ cursor: "pointer" }}
                    onClick={(st) => { const k = st?.activePayload?.[0]?.payload?.key || (st?.activeLabel ? rows.find((r) => r.label === st.activeLabel)?.key : null); if (k) setPinned((p) => (p === k ? null : k)); }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={compact} width={64} />
                    <Tooltip cursor={{ fill: "hsl(206,30%,92%)", opacity: 0.5 }} content={<BarTooltip fyLabel={fyLabel} prevLabel={prevLabel} testIdPrefix={testIdPrefix} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="text-foreground">{v}</span>} />
                    <Bar dataKey="current_total" name={`FY ${fyLabel}`} fill={COLOR_CURRENT} radius={[4, 4, 0, 0]} maxBarSize={38} isAnimationActive={false} />
                    {hasPrev && <Bar dataKey="previous_total" name={`FY ${prevLabel} (previous)`} fill={COLOR_PREVIOUS} radius={[4, 4, 0, 0]} maxBarSize={38} isAnimationActive={false} />}
                    {hasTargets && (
                      <Line type="linear" dataKey="target" name="Monthly target" stroke={COLOR_TARGET} strokeWidth={2} strokeDasharray="6 4" connectNulls={false}
                        dot={{ r: 3.5, fill: COLOR_TARGET, strokeWidth: 0 }} activeDot={{ r: 5, fill: COLOR_TARGET, stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} legendType="plainline" />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {pinnedRow && (
              <div className="mt-2 rounded-lg border bg-muted/30 p-3 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`${testIdPrefix}-pinned`} data-month={pinnedRow.key}>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground sm:w-40 shrink-0">
                  <Pin className="h-3.5 w-3.5" /> Selected month — click the bar again or <button type="button" className="underline hover:text-foreground" onClick={() => setPinned(null)} data-testid={`${testIdPrefix}-pinned-clear`}>clear</button>
                </div>
                <div className={`flex-1 grid grid-cols-1 gap-3 ${pinnedRow.previous && pinnedRow.target != null ? "md:grid-cols-3" : pinnedRow.previous || pinnedRow.target != null ? "md:grid-cols-2" : ""}`}>
                  {[{ key: "current", title: fyLabel, color: COLOR_CURRENT, d: pinnedRow.current, when: pinnedRow.key },
                    ...(pinnedRow.previous ? [{ key: "previous", title: prevLabel, color: COLOR_PREVIOUS, d: pinnedRow.previous, when: pinnedRow.previous.key }] : [])].map((b) => (
                    <div key={b.key} className="rounded-md border bg-background p-2.5 text-xs" data-testid={`${testIdPrefix}-pinned-${b.key}`}>
                      <p className="font-medium flex items-center gap-1.5 mb-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} /> {pinnedRow.month_name} · FY {b.title} <span className="text-muted-foreground font-normal">({b.when})</span></p>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 tabular-nums">
                        <dt className="text-muted-foreground">Total receipts</dt><dd className="text-right font-medium" data-testid={`${testIdPrefix}-pinned-${b.key}-receipts`}>{Number(b.d.receipts || 0).toLocaleString("en-IN")}</dd>
                        <dt className="text-muted-foreground">Total collected</dt><dd className="text-right font-semibold" data-testid={`${testIdPrefix}-pinned-${b.key}-total`}>{money(b.d.total)}</dd>
                        <dt className="text-muted-foreground">Third party</dt><dd className="text-right" data-testid={`${testIdPrefix}-pinned-${b.key}-third-party`}>{money(b.d.third_party)}</dd>
                        <dt className="text-muted-foreground">Direct collection</dt><dd className="text-right" data-testid={`${testIdPrefix}-pinned-${b.key}-direct`}>{money(b.d.direct)}</dd>
                      </dl>
                    </div>
                  ))}
                  {pinnedRow.target != null && (
                    <div className="rounded-md border bg-background p-2.5 text-xs" data-testid={`${testIdPrefix}-pinned-target`}>
                      <p className="font-medium flex items-center gap-1.5 mb-1"><span className="inline-block h-0.5 w-3 rounded-sm" style={{ background: COLOR_TARGET }} /> {pinnedRow.month_name} · Target FY {fyLabel}</p>
                      <GapLine row={pinnedRow} testId={`${testIdPrefix}-pinned-target-detail`} />
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 self-start" onClick={() => setPinned(null)} title="Clear selection" data-testid={`${testIdPrefix}-pinned-close`}><X className="h-3.5 w-3.5" /></Button>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 mt-2 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1" data-testid={`${testIdPrefix}-last-updated`} title={lastAt || ""}>
                <Clock className="h-3 w-3" /> Updated {timeAgo(lastAt)} · refreshes automatically every 60 s
              </span>
              <span>Successful receipts by payment date · third party = receipts on properties created through Third Party entry{hasTargets && tg.updated_by_name ? ` · targets set by ${tg.updated_by_name}` : ""}</span>
            </div>
          </>
        )}
      </CardContent>

      {/* Phase 214: Set / edit monthly targets for the FY shown */}
      {canEditTargets && (
        <Dialog open={targetsOpen} onOpenChange={setTargetsOpen}>
          <DialogContent className="max-w-3xl" data-testid={`${testIdPrefix}-targets-dialog`}>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2"><Target className="h-4 w-4" /> Monthly collection targets</DialogTitle>
              <DialogDescription>
                Set the collection target (₹) for each month of the Financial Year. Saved targets are drawn as the dashed line on the graph and the
                tooltip shows the gap for every month. Blank months carry no target.
              </DialogDescription>
            </DialogHeader>
            {targetsOpen && (
              <CollectionTargetsEditor code={code} fyId={data?.fy?.id || ""} testIdPrefix={`${testIdPrefix}-targets`}
                onSaved={(p) => { if (p?.fy?.id && p.fy.id !== fyRef.current) { fyRef.current = p.fy.id; setFyId(p.fy.id); } else load(true); setTargetsOpen(false); }} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
